import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./lnlApi";

const AuthContext = createContext(null);
const TOKEN_KEY = "lnl_admin_jwt";
const LEGACY_KEY = "lnl_admin_token";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

    const applyHeaders = useCallback((jwt) => {
        if (jwt) api.defaults.headers.common["Authorization"] = `Bearer ${jwt}`;
        else delete api.defaults.headers.common["Authorization"];
    }, []);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        applyHeaders(token);
        if (token) { refreshMe(); }
        else { setUser(null); }
    }, [token, applyHeaders, refreshMe]);

    async function login(email, password) {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.removeItem(LEGACY_KEY);
        setToken(data.token);
        setUser(data.user);
        return data.user;
    }

    function logout() {
        // Fire-and-forget audit log; JWT is stateless so the actual sign-out is local.
        api.post("/auth/logout").catch(() => {});
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(LEGACY_KEY);
        setToken(null);
        setUser(null);
        applyHeaders(null);
    }

    async function changePassword(current_password, new_password) {
        await api.post("/auth/change-password", { current_password, new_password });
    }

    return (
        <AuthContext.Provider value={{ user, token, login, logout, refreshMe, changePassword }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export const ROLE_LABEL = {
    super_admin: "Super Administrator",
    website_admin: "Website Administrator",
    content_editor: "Content Editor",
    admissions_officer: "Admissions Officer",
    document_reviewer: "Document Reviewer",
    department_rep: "Department Representative",
    analyst: "Analyst",
};

export const CAN_MANAGE_USERS = new Set(["super_admin", "website_admin"]);
export const CAN_WRITE = new Set(["super_admin", "website_admin", "admissions_officer", "content_editor", "document_reviewer"]);
